import { Button, Icon } from '@zillow/constellation-10';
import { IconTourDoorFilled } from '@zillow/constellation-icons';

import { LoadingHeading } from '@zg-rentals/loading-components';
import {
  ToursSummary,
  type ToursSummaryDayChipType,
  type ToursSummaryTileType,
  UpcomingTours,
  useHasFutureTourAvailability,
  useListingTourConfig,
  useTouringTreatmentForZuidAndListing,
  useUpcomingToursData,
} from '@zg-rentals/touring';

import { useScreenSize } from '../../../../hooks/useScreenSize';

import accessUtils from '../../../../utils/accessUtils';

import { getTourCalendarRoute } from '../../../../constants/urls';

import { useAuth } from '../../../../components/AuthProvider';
import EmptyState from '../../../../components/EmptyState';

import {
  emitTourSchedulingUpsellSetTourAvailabilityCtaEvent,
  emitToursSummaryDayChipClickEvent,
  emitToursSummaryImpressionEvent,
  emitToursSummaryTileClickEvent,
  emitToursSummaryTourCardClickEvent,
  emitUpcomingToursMoreToursCtaEvent,
  TOUR_SCHEDULING_UPSELL_SET_TOUR_AVAILABILITY_CTA_EVENT_ID,
  UPCOMING_TOURS_MORE_TOURS_CTA_EVENT_ID,
} from '../../../../analytics/events/overviewPage';
import ConnectedModuleCard from '../../../ConnectedModuleCard';

type Props = {
  listingAlias: string;
  isActive: boolean;
};

const UpcomingToursModule = ({ listingAlias, isActive }: Props) => {
  const { user } = useAuth();
  const { isScreenMobile } = useScreenSize();

  const inquiriesPath = `/rental-manager/lead-management/${listingAlias}`;
  const { pathname: tourCalendarPathname } = getTourCalendarRoute({ listingAlias });
  const tourCalendarPath = `/rental-manager${tourCalendarPathname.replace('[listingAlias]', listingAlias)}`;

  const isRedesignEnabled = accessUtils.canAccessUpcomingToursRedesign();

  const { data: touringTreatment, isLoading: isGetTouringTreatmentLoading } = useTouringTreatmentForZuidAndListing(
    { listingAlias },
    { enabled: Boolean(listingAlias), errorDetails: 'UpcomingToursModule - Error fetching touring treatment' },
  );

  const isTouringEnabled = Boolean(touringTreatment?.isTouringEnabledForListing);
  const hasBookedTours = Boolean(touringTreatment?.hasBookedTours);

  // Shares a query key with the widget's own call, so this gate costs no extra request.
  const { hasTourConfig } = useListingTourConfig(listingAlias, { enabled: isRedesignEnabled });

  // A slow/errored config or treatment keeps the card rather than flashing it in and out as they settle.
  const canRenderRedesignCard = (isActive || hasBookedTours) && hasTourConfig !== false;

  // Extra request (no shared key); skip when a booked tour already short-circuits the empty-state check.
  const {
    hasFutureAvailability,
    isError: isAvailabilityError,
    isLoading: isAvailabilityLoading,
    isPending: isAvailabilityPending,
  } = useHasFutureTourAvailability(listingAlias, {
    enabled: isTouringEnabled && (!isRedesignEnabled || (canRenderRedesignCard && !hasBookedTours)),
  });

  // Also shares a query key with the widget's own call — used only to decide the empty state here.
  const {
    counts,
    isError: isToursDataError,
    isPending: isToursDataPending,
  } = useUpcomingToursData(listingAlias, {
    enabled: isRedesignEnabled && isTouringEnabled && canRenderRedesignCard,
  });

  const onCalendarClick = () => {
    if (user) {
      emitUpcomingToursMoreToursCtaEvent({ currentProperty: { listingAlias }, user });
    }
  };

  const handleToursSummaryImpression = () => {
    if (user) {
      emitToursSummaryImpressionEvent({ currentProperty: { listingAlias }, user });
    }
  };

  const handleToursSummaryTileClick = (tile: ToursSummaryTileType) => {
    if (user) {
      emitToursSummaryTileClickEvent({ currentProperty: { listingAlias }, user, tile });
    }
  };

  const handleToursSummaryDayChipClick = (chip: ToursSummaryDayChipType) => {
    if (user) {
      emitToursSummaryDayChipClickEvent({ currentProperty: { listingAlias }, user, chip });
    }
  };

  const handleToursSummaryTourCardClick = () => {
    if (user) {
      emitToursSummaryTourCardClickEvent({ currentProperty: { listingAlias }, user });
    }
  };

  const handleTourSchedulingUpsellClick = () => {
    if (user) {
      emitTourSchedulingUpsellSetTourAvailabilityCtaEvent({ currentProperty: { listingAlias }, user });
    }
  };

  // Shared by both flag states: touring is on, but with nothing to show and no way to book, prompt the
  // landlord to set availability instead of rendering an empty-looking widget.
  const emptyState = (
    <EmptyState
      data-testid="EmptyUpcomingToursCard"
      icon={<Icon size="md" css={{ color: 'icon.subtle' }} render={<IconTourDoorFilled />} />}
      heading="No tours scheduled yet"
      body="Set your tour availability so renters can book a tour."
      align="left"
      useCard={false}
      actions={
        <Button tone="brand" emphasis="filled" asChild>
          <a
            href={tourCalendarPath}
            onClick={handleTourSchedulingUpsellClick}
            data-eventid={TOUR_SCHEDULING_UPSELL_SET_TOUR_AVAILABILITY_CTA_EVENT_ID}
          >
            Set tour availability
          </a>
        </Button>
      }
    />
  );

  // Only a confirmed negative hides the card; a slow/errored gate leaves it in place.
  if (isRedesignEnabled) {
    // Touring being off for the listing sends the tour calendar to tour-availability, which drops the
    // deep-linked tourId, so the card stays hidden there just as it is on the flag-off path.
    const isTouringOffForListing = Boolean(touringTreatment) && !isTouringEnabled;

    if (!canRenderRedesignCard || isTouringOffForListing) {
      return null;
    }

    const hasAnyTourEvents = counts.requested > 0 || counts.booked > 0 || counts.canceled > 0;
    // isPending rather than isLoading: a paused query (e.g. offline) is not fetching but has no data
    // either, and must not read as a settled zero.
    const areGateQueriesSettled =
      !isAvailabilityPending && !isAvailabilityError && !isToursDataPending && !isToursDataError;
    // All conditions must be confirmed positives: a pending config could resolve to false and unmount the card.
    const isConfirmedEmpty =
      isTouringEnabled &&
      hasTourConfig === true &&
      areGateQueriesSettled &&
      !hasBookedTours &&
      !hasAnyTourEvents &&
      !hasFutureAvailability;

    // The treatment and availability gates settle after the widget's own queries, so a settled widget
    // could still unmount (touring off) or swap to the empty state; hold the impression until it can't.
    const couldStillBeReplaced =
      isGetTouringTreatmentLoading ||
      (isTouringEnabled &&
        hasTourConfig === true &&
        !hasBookedTours &&
        !hasAnyTourEvents &&
        !hasFutureAvailability &&
        isAvailabilityPending);

    // Matches the redirect predicate in tour-calendar.tsx; errored treatment fails closed.
    const canDeepLinkToCalendar =
      isGetTouringTreatmentLoading || hasBookedTours || Boolean(touringTreatment?.hasAvailability);

    return (
      <ConnectedModuleCard css={{ borderRadius: 'obj.default' }}>
        {isConfirmedEmpty ? (
          emptyState
        ) : (
          <ToursSummary
            listingAlias={listingAlias}
            tourCalendarPath={tourCalendarPath}
            inquiriesPath={inquiriesPath}
            showCancelledTours={accessUtils.canShowCancelledTours()}
            canDeepLinkToCalendar={canDeepLinkToCalendar}
            onImpression={user && !couldStillBeReplaced ? handleToursSummaryImpression : undefined}
            onViewCalendarClick={onCalendarClick}
            onTileClick={handleToursSummaryTileClick}
            onDayChipClick={handleToursSummaryDayChipClick}
            onTourCardClick={handleToursSummaryTourCardClick}
            viewCalendarEventId={UPCOMING_TOURS_MORE_TOURS_CTA_EVENT_ID}
          />
        )}
      </ConnectedModuleCard>
    );
  }

  const isLoading = isGetTouringTreatmentLoading || (isTouringEnabled && isAvailabilityLoading);

  if (isLoading) {
    return (
      <ConnectedModuleCard
        isLoading
        css={{
          borderRadius: 'obj.default',
        }}
      >
        <LoadingHeading level={2} isLoading loadingWidth="100%">
          Upcoming Tours
        </LoadingHeading>
      </ConnectedModuleCard>
    );
  }

  if (!touringTreatment || !isTouringEnabled || !(isActive || hasBookedTours)) {
    return null;
  }

  // Touring is enabled, but with no upcoming availability and no booked tours the "Upcoming tours"
  // widget would render empty and misleading — prompt the user to set availability instead.
  const hasUpcomingContent = hasFutureAvailability || hasBookedTours;

  return (
    <ConnectedModuleCard
      css={{
        borderRadius: 'obj.default',
      }}
    >
      {hasUpcomingContent ? (
        <UpcomingTours
          inquiriesPath={inquiriesPath}
          isScreenMobile={isScreenMobile}
          listingAlias={listingAlias}
          onCalendarClick={onCalendarClick}
          tourCalendarPath={tourCalendarPath}
          eventId={UPCOMING_TOURS_MORE_TOURS_CTA_EVENT_ID}
        />
      ) : (
        emptyState
      )}
    </ConnectedModuleCard>
  );
};

export default UpcomingToursModule;
